import * as React from 'react';
import { LocalForm, Control, Errors } from 'react-redux-form';
import { Link } from 'react-router-dom';
import ReactTable, { Column } from 'react-table';

import { ModalProps, CheckboxInline } from '@common/components';
import { getRoute, RouteDefinitions } from '@common/route-definitions';
import { getDefaultErrorMessages } from '@common/validators';
import { CancelledContractModel } from '@models/ContractCancellation';
import { ProfileViewModel } from '@models/Auth';

import { AccountingOwnership } from './accounting-ownership.component';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';

interface FormModel {
  readonly query: string;
  readonly disbursed: boolean;
}

export interface AccountingListComponentProperties {
  readonly cancelledContractsList: Array<CancelledContractModel>;
  readonly filterByDisbursed: boolean;
  readonly message: string;
  readonly loading?: boolean;
  readonly pageCount: number;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly query: string | null;
  readonly user: ProfileViewModel | null;
  readonly handleOwnershipChange: (
    contractId: number,
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    filterByDisbursed: boolean,
    release?: boolean
  ) => void;
  readonly searchCancelledContractsList: (
    query: string | null,
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    filterByDisbursed: boolean
  ) => void;
  readonly toggleModal: (modalProps?: ModalProps) => void;
}

export class AccountingListComponent extends React.Component<AccountingListComponentProperties> {
  constructor(props: any) {
    super(props);
    this.handleFetchData = this.handleFetchData.bind(this);
  }

  public render(): JSX.Element {
    const columns: Column[] = [
      {
        accessor: (m: CancelledContractModel) =>
          m ? (
            <a className={this.props.loading ? 'disabled' : 'active'} onClick={() => this.handleOwnershipClick(m)}>
              <i className="fas fa-fw fa-hand-paper" />
            </a>
          ) : (
            'N/A'
          ),
        id: 'link',
        Header: 'Ownership',
        sortable: false,
        width: 80
      },
      {
        accessor: 'owner.value',
        Header: 'Owner',
        sortable: false
      },
      {
        accessor: (m: CancelledContractModel) =>
          m ? (
            <Link
              to={getRoute(RouteDefinitions.contract.Details, {
                id: +m.contractId!
              })}
              target="_blank"
            >
              {' '}
              {m.contractNumber}{' '}
            </Link>
          ) : (
            'N/A'
          ),
        id: 'contract',
        Header: 'Contract #',
        sortable: false,
        width: 100
      },
      {
        accessor: 'customer.name',
        Header: 'Customer Name',
        sortable: false
      },
      {
        accessor: (p: CancelledContractModel) => (p.refundAmount ? '$' + p.refundAmount.toFixed(2) : ''),
        id: 'refundAmount',
        Header: 'Refund Amount',
        sortable: false
      },
      {
        accessor: 'paymentType.value',
        Header: 'Payment Type',
        sortable: false
      },
      {
        accessor: (m: CancelledContractModel) =>
          m.refundAddress
            ? m.refundAddress.line1 +
              ', ' +
              (m.refundAddress.line2 ? m.refundAddress.line2 + ', ' : '') +
              m.refundAddress.city +
              ', ' +
              (m.refundAddress.state ? m.refundAddress.state.abbreviation : '') +
              '  ' +
              m.refundAddress.zip
            : '',
        id: 'refundAddress',
        Header: 'Override Address',
        sortable: false
      }
    ];
    return (
      <div className="row">
        <div className="col-md-12">
          <LocalForm
            className="form-horizontal"
            hideNativeErrors={true}
            onSubmit={(props: FormModel) => this.handleSearch(props.query, props.disbursed)}
          >
            <div className="row">
              <div className="col-md-12">
                <h2>Cancelled Contracts</h2>
              </div>
              <div className="form-control col-md-12">
                <label htmlFor="local.query">Search</label>
                <TooltipComponent
                  text="Search by Contract#, Contract Number, Customer Name, Contract Address"
                  side="right"
                />
                <Control.text model=".query" />
                <Errors
                  className="error"
                  model=".query"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
                {this.props.pageCount * 10 > 10000 && (
                  <span> Your search is too broad. Narrow the search with more specific terms. </span>
                )}
              </div>
            </div>
            <div className="row center-xs">
              <div className="form-control mr-10">
                <CheckboxInline className="end-xs">
                  <Control.checkbox model=".disbursed" />
                  <label htmlFor="local.disbursed">Show Disbursed Only</label>
                </CheckboxInline>
              </div>
              <button className="btn btn-primary" disabled={this.props.loading} type="submit">
                Search
              </button>
            </div>
          </LocalForm>
          <ReactTable
            onPageChange={() => {
              Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                element.scrollTop = 0;
              });
            }}
            className="-striped -highlight"
            columns={columns}
            data={this.props.cancelledContractsList}
            defaultPageSize={10}
            loading={this.props.loading}
            manual={true}
            onFetchData={this.handleFetchData}
            pages={this.props.pageCount}
          />
        </div>
      </div>
    );
  }

  private handleOwnershipClick(row: CancelledContractModel): void {
    if (this.props.user && row.owner && row.owner.id && !this.props.loading) {
      if (this.props.user.id !== row.owner.id) {
        this.props.toggleModal({
          title: 'Ownership Confirmation',
          isSmall: true,
          component: AccountingOwnership,
          saveLabel: 'Yes',
          closeLabel: 'No',
          saveCallback: () => this.submitOwnershipChange(row.contractId),
          closeCallback: this.props.toggleModal,
          childProps: {}
        });
      } else if (this.props.user && row.owner && this.props.user.id === +row.owner.id) {
        this.submitOwnershipChange(row.contractId, true);
      }
    } else if (!this.props.loading) {
      this.submitOwnershipChange(row.contractId);
    }
  }

  private handleFetchData(state: {
    page: number;
    pageSize: number;
    sorted: Array<{ id: string; desc: string }>;
  }): void {
    const orderBy = state.sorted[0] ? state.sorted[0].id : 'cancellationId';
    const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'desc' : 'asc') : 'asc';

    this.props.searchCancelledContractsList(
      this.props.query,
      state.pageSize,
      state.page + 1,
      orderBy,
      orderDirection,
      this.props.filterByDisbursed
    );
  }

  private handleSearch = (query: string, filterByDisbursed: boolean): void => {
    this.props.searchCancelledContractsList(query, this.props.pageSize, 1, 'cancellationId', 'asc', filterByDisbursed);
  };

  private submitOwnershipChange = (contractId: number, release?: boolean): void => {
    this.props.handleOwnershipChange(
      contractId,
      this.props.query,
      this.props.pageSize,
      this.props.pageNumber,
      'cancellationId',
      'asc',
      this.props.filterByDisbursed,
      release
    );
  };
}



// WEBPACK FOOTER //
// ./src/accounting/_components/accounting-list/accounting-list.component.tsx